"use client";

import * as React from "react";
import Image from "next/image";
import CtaButton from "@/components/ui/CtaButton";
import { LazyMotion, domAnimation, m, type HTMLMotionProps } from "framer-motion";

export type WhyAboutClientProps = HTMLMotionProps<"section"> & {
    heading: string;
    description: string;
    benefits: {
        heading: string;
        description: string;
        icon: { src: string; blur?: string } | null;
    }[];
    testimonial: {
        quote: string;
        author: string;
        image: { src: string; blur?: string; sizes: string; alt: string } | null;
    } | null;
};

const slideUp = {
    hidden: { y: 25, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1], delay: i * 0.1 },
    }),
} as const;

const imageReveal = {
    hidden: { opacity: 0, scale: 0.98 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 1, ease: "easeOut", delay: 0.2 },
    },
} as const;

export default function WhyAboutClient({
                                           heading,
                                           description,
                                           benefits,
                                           testimonial,
                                           className,
                                           ...rest
                                       }: WhyAboutClientProps) {
    return (
        <LazyMotion features={domAnimation}>
            <m.section
                id="relume"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className={`bg-secondary px-[1%] py-16 text-primary md:py-24 lg:py-14 rounded-3xl mb-20 ${className ?? ""}`}
                {...rest}
            >
                {/* ZMIANA 1: Zmniejszyłem padding na tablecie (md:px-8 zamiast md:px-12), żeby zyskać miejsce */}
                <div className="container px-6 py-6 md:px-8 md:py-8 lg:px-16">
                    {/* Header */}
                    <div className="mx-auto max-w-lg text-center">
                        <m.h2
                            variants={slideUp}
                            custom={0}
                            className="whitespace-pre-line text-3xl font-medium leading-tight md:text-6xl"
                        >
                            {heading}
                        </m.h2>

                        <m.p
                            variants={slideUp}
                            custom={1}
                            className="mt-4 whitespace-pre-line mx-auto max-w-md text-primary"
                        >
                            {description}
                        </m.p>

                        <m.div variants={slideUp} custom={2} className="mt-8 flex justify-center">
                            <CtaButton />
                        </m.div>
                    </div>

                    {/* Benefits row */}
                    {/* ZMIANA 2: md:gap-4 (mniejszy odstęp między kolumnami na tablecie) */}
                    <div className="mt-12 grid grid-cols-1 gap-10 md:mt-28 md:grid-cols-3 md:gap-4 lg:gap-0">
                        {benefits.map((item, idx) => (
                            <m.div
                                key={`${item.heading}-${idx}`}
                                variants={slideUp}
                                custom={idx + 3}
                                // ZMIANA 3: md:px-3 (mniejszy padding wewnątrz kolumny na tablecie)
                                className={`flex flex-col items-stretch md:px-3 lg:px-12 ${
                                    idx !== 0 ? "md:border-l md:border-text-alternative/25" : ""
                                }`}
                            >
                                {/* ikona ZAWSZE po lewej */}
                                <div className="flex items-center gap-4 md:items-start lg:items-center">
                                    <div className="shrink-0 md:pt-1 lg:pt-0">
                                        {item.icon?.src ? (
                                            <Image
                                                src={item.icon.src}
                                                alt={item.heading ?? ""}
                                                width={44}
                                                height={44}
                                                loading="lazy"
                                                decoding="async"
                                                draggable={false}
                                                placeholder={item.icon.blur ? "blur" : "empty"}
                                                blurDataURL={item.icon.blur}
                                                // ZMIANA 4: Skalowanie ikony na tablecie (md:w-9) żeby pasowała do mniejszego tekstu
                                                className="opacity-90 md:w-9 md:h-9 lg:w-[44px] lg:h-[44px]"
                                            />
                                        ) : null}
                                    </div>

                                    {/* ZMIANA 5: md:text-base (mniejszy nagłówek na tablecie), lg:text-xl (powrót na desktopie) */}
                                    <h3 className="whitespace-pre-line text-lg font-medium leading-snug text-primary md:text-base lg:text-xl">
                                        {item.heading}
                                    </h3>
                                </div>

                                {/* opis */}
                                {/* ZMIANA 6: md:text-[11px] (mniejszy opis na tablecie), lg:text-sm (powrót na desktopie) */}
                                <p className="mt-6 text-sm leading-relaxed text-primary md:text-[11px] md:max-w-none lg:text-sm lg:max-w-xs">
                                    {item.description}
                                </p>
                            </m.div>
                        ))}
                    </div>

                    {/* Testimonial image */}
                    <m.div variants={imageReveal} className="mt-12 md:mt-16">
                        <div className="relative overflow-hidden rounded-2xl group">
                            {/* Tło: obraz + przyciemnienie */}
                            <div className="absolute inset-0">
                                {testimonial?.image?.src ? (
                                    <Image
                                        src={testimonial.image.src}
                                        alt={testimonial.image.alt ?? ""}
                                        fill
                                        sizes={testimonial.image.sizes}
                                        loading="lazy"
                                        decoding="async"
                                        draggable={false}
                                        placeholder={testimonial.image.blur ? "blur" : "empty"}
                                        blurDataURL={testimonial.image.blur}
                                        className="object-cover transition-transform duration-1000 group-hover:scale-105"
                                    />
                                ) : null}
                                <div className="absolute inset-0 bg-black/45" />
                            </div>

                            {/* Kontener, który MOŻE rosnąć z treścią */}
                            <div className="relative z-10 w-full min-h-[360px] sm:min-h-[260px] md:min-h-[320px] flex items-center">
                                <m.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.8, delay: 0.6 }}
                                    className="max-w-3xl px-6 py-8 md:px-10"
                                >
                                    <p className="leading-relaxed text-primary-light italic">
                                        "{testimonial?.quote ?? ""}"
                                    </p>
                                    <p className="mt-6 text-sm font-semibold tracking-[0.08em] uppercase">
                                        - {testimonial?.author ?? ""}
                                    </p>
                                </m.div>
                            </div>
                        </div>
                    </m.div>
                </div>
            </m.section>
        </LazyMotion>
    );
}